import { useMemo } from 'react';

import { getReadingDeviceKind, isAcceptedReading } from './kioskTransforms';
import { usePalletProductLookup } from './usePalletProductLookup';

export function useSelectedKioskReadings({
  deviceMap,
  displayedReadings,
  requestSummary,
  selectedBinding,
}) {
  const latestScannerAttempt = useMemo(
    () => displayedReadings.find((reading) => (
      getReadingDeviceKind(reading, deviceMap) === 'Scanner'
    )) ?? null,
    [deviceMap, displayedReadings],
  );
  const scannerReading = useMemo(
    () => displayedReadings.find((reading) => (
      getReadingDeviceKind(reading, deviceMap) === 'Scanner' && isAcceptedReading(reading)
    )) ?? (
      requestSummary?.palletValue && requestSummary.palletValue !== 'Pending'
        ? {
          deviceId: selectedBinding?.scannerDeviceId ?? '',
          readingStatus: 'Accepted',
          readingValue: requestSummary.palletValue,
        }
        : null
    ),
    [deviceMap, displayedReadings, requestSummary?.palletValue, selectedBinding?.scannerDeviceId],
  );
  const scaleReading = useMemo(
    () => displayedReadings.find((reading) => (
      getReadingDeviceKind(reading, deviceMap) === 'Scale' && isAcceptedReading(reading)
    )) ?? (
      requestSummary?.scaleValue && requestSummary.scaleValue !== 'Pending'
        ? {
          deviceId: selectedBinding?.scaleDeviceId ?? '',
          readingStatus: 'Accepted',
          readingValue: requestSummary.scaleValue,
        }
        : null
    ),
    [deviceMap, displayedReadings, requestSummary?.scaleValue, selectedBinding?.scaleDeviceId],
  );
  const scannerPalletCode = useMemo(
    () => String(scannerReading?.readingValue ?? '').trim(),
    [scannerReading?.readingValue],
  );
  const scannerStoredProductName = useMemo(
    () => String(
      scannerReading?.scannedProduct?.name
      ?? scannerReading?.scannedProductName
      ?? requestSummary?.palletProductName
      ?? '',
    ).trim(),
    [
      requestSummary?.palletProductName,
      scannerReading?.scannedProduct?.name,
      scannerReading?.scannedProductName,
    ],
  );
  const scannerStoredQuantity = useMemo(() => {
    const quantity = scannerReading?.scannedProduct?.quantityPacks
      ?? scannerReading?.scannedProduct?.quantity;
    return Number.isFinite(quantity) ? quantity : null;
  }, [scannerReading?.scannedProduct?.quantity, scannerReading?.scannedProduct?.quantityPacks]);
  const scannerProductLabel = usePalletProductLookup({
    palletCode: scannerPalletCode,
    storedProductName: scannerStoredProductName,
  });

  return {
    scannerErrorMessage: latestScannerAttempt?.readingStatus === 'Rejected'
      ? latestScannerAttempt.errorMessage ?? ''
      : '',
    scannerPalletCode,
    scannerProductLabel,
    scannerReading,
    scannerStoredQuantity,
    scaleReading,
  };
}
